import {
  ComponentProps,
  FocusEvent,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
} from 'react';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { TextField } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature } from 'src/WebApiController';

export type PosTextFieldProps = ComponentProps<typeof TextField.Input> & {
  trimOnBlur?: boolean;
  prefixDisplay?: ReactNode;
  postfixDisplay?: ReactNode;
  rootProps?: Omit<ComponentProps<typeof TextField.Root>, 'children'>;
  errorState?: ComponentProps<typeof TextField.Root>['state'] | undefined;
  inheritBackground?: boolean;
};

export const PosTextField = forwardRef<HTMLInputElement, PosTextFieldProps>(
  function PosTextField(
    {
      onBlur,
      onKeyUp,
      onChange,
      trimOnBlur,
      prefixDisplay,
      postfixDisplay,
      rootProps,
      errorState,
      inheritBackground,
      ...props
    },
    ref
  ): ReactElement {
    // We should not add the feature check at the base component
    // But add it here as this is a high risk change, that impacts all the number inputs
    const hasNumberInputWithLeadingZeroFeature = useUserHasFeature(
      Feature.NumberInputWithLeadingZero
    );
    const customOnBlur = useCallback(
      (params: FocusEvent<HTMLInputElement>) => {
        if (trimOnBlur) {
          params.target.value = params.target.value.trim();
        }
        if (onBlur) {
          onBlur(params);
        }
      },
      [trimOnBlur, onBlur]
    );

    const onChangeHandler = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange == null) {
          return;
        }
        if (hasNumberInputWithLeadingZeroFeature && props.type === 'number') {
          // Strip leading zeros to prevent values like "01" when editing from "0"
          const raw = e.target.value;
          if (raw.length > 1 && raw[0] === '0' && raw[1] !== '.') {
            const parsedValue = parseFloat(raw);
            if (!isNaN(parsedValue)) {
              e.target.value = parsedValue.toString();
            }
          }
        }
        onChange(e);
      },
      [hasNumberInputWithLeadingZeroFeature, onChange, props.type]
    );

    return (
      <TextField.Root
        disabled={props.disabled}
        {...rootProps}
        state={errorState}
        inheritBackground={inheritBackground}
      >
        {prefixDisplay}
        <TextField.Input
          ref={ref}
          {...props}
          onChange={onChangeHandler}
          onBlur={customOnBlur}
          onKeyUp={onKeyUp}
        />
        {postfixDisplay}
      </TextField.Root>
    );
  }
);

export const getTextFieldState = (
  error?:
    | string
    | string[]
    | FieldError
    | FieldError[]
    | Merge<FieldError, FieldErrorsImpl>
): ComponentProps<typeof TextField.Root>['state'] | undefined => {
  if (!error) return undefined;

  const isArray = Array.isArray(error);

  return (isArray ? error.length : error) ? 'error' : undefined;
};
